import {
	FilterOutlined,
	ReloadOutlined,
	SearchOutlined,
} from "@ant-design/icons";
import {
	Button,
	Card,
	Col,
	Empty,
	Input,
	Row,
	Select,
	Space,
	Spin,
	Table,
	Tag,
	Tooltip,
	Typography,
} from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { getAllSalesPrices } from "../../services/salesPriceService";

const { Title } = Typography;
const { Option } = Select;

const SalesPriceList = () => {
	const [salesPrices, setSalesPrices] = useState([]);
	const [loading, setLoading] = useState(true);
	const [filteredData, setFilteredData] = useState([]);
	const [searchText, setSearchText] = useState("");
	const [filterSalesType, setFilterSalesType] = useState("");
	const [filterCurrency, setFilterCurrency] = useState("");
	const isMobile = window.innerWidth < 768;

	// Cargar precios de venta al inicio
	useEffect(() => {
		loadSalesPrices();
	}, []);

	const filterData = useCallback(() => {
		let filtered = [...salesPrices];

		// Filtro por texto de búsqueda
		if (searchText) {
			filtered = filtered.filter(
				(item) =>
					item.Item_No?.toLowerCase().includes(searchText.toLowerCase()) ||
					item.Sales_Code?.toLowerCase().includes(searchText.toLowerCase()) ||
					item.Unit_of_Measure_Code?.toLowerCase().includes(
						searchText.toLowerCase()
					) ||
					item.Variant_Code?.toLowerCase().includes(searchText.toLowerCase())
			);
		}

		// Filtro por tipo de venta
		if (filterSalesType) {
			filtered = filtered.filter((item) => item.Sales_Type === filterSalesType);
		}

		// Filtro por moneda
		if (filterCurrency) {
			filtered = filtered.filter(
				(item) => item.Currency_Code === filterCurrency
			);
		}

		setFilteredData(filtered);
	}, [salesPrices, searchText, filterSalesType, filterCurrency]);

	// Filtrar datos cuando cambien los filtros
	useEffect(() => {
		filterData();
	}, [filterData]);

	const loadSalesPrices = async () => {
		try {
			console.log("Cargando precios de venta...");
			setLoading(true);
			const priceList = await getAllSalesPrices();
			setSalesPrices(priceList);
		} catch (error) {
			window.messageApi.error(
				"Error al cargar precios de venta: " + error.message
			);
		} finally {
			setLoading(false);
		}
	};

	const clearFilters = () => {
		setSearchText("");
		setFilterSalesType("");
		setFilterCurrency("");
	};

	// Obtener valores únicos para los filtros
	const uniqueSalesTypes = [
		...new Set(salesPrices.map((item) => item.Sales_Type).filter(Boolean)),
	];
	const uniqueCurrencies = [
		...new Set(salesPrices.map((item) => item.Currency_Code).filter(Boolean)),
	];

	// Formatear fecha
	const formatDate = (dateString) => {
		if (!dateString) return "-";
		try {
			return new Date(dateString).toISOString().split('T')[0];
		} catch {
			return dateString;
		}
	};

	// Formatear precio
	const formatPrice = (price, currency) => {
		if (price === null || price === undefined) return "-";
		const symbol =
			currency === "EUR"
				? "$"
				: currency === "USD"
					? "$"
					: currency === "BS"
						? "Bs."
						: "";
		return `${symbol}${Number(price).toFixed(2)}`;
	};

	const columns = [
		{
			title: "No. Artículo",
			dataIndex: "Item_No",
			key: "Item_No",
			width: 120,
			fixed: isMobile ? false : "left",
			render: (text) => text || "-",
		},
		{
			title: "Tipo de Venta",
			dataIndex: "Sales_Type",
			key: "Sales_Type",
			width: 120,
			render: (text) => text || "-",
		},
		{
			title: "Código de Venta",
			dataIndex: "Sales_Code",
			key: "Sales_Code",
			width: 120,
			render: (text) => text || "-",
		},
		{
			title: "Fecha Inicio",
			dataIndex: "Starting_Date",
			key: "Starting_Date",
			width: 110,
			render: (date) => formatDate(date),
		},
		{
			title: "Fecha Fin",
			dataIndex: "Ending_Date",
			key: "Ending_Date",
			width: 110,
			render: (date) => formatDate(date),
		},
		{
			title: "Moneda",
			dataIndex: "Currency_Code",
			key: "Currency_Code",
			width: 80,
			render: (currency) =>
				currency ? (
					<Tag
						color={
							currency === "EUR"
								? "blue"
								: currency === "USD"
									? "green"
									: "orange"
						}
					>
						{currency}
					</Tag>
				) : (
					"-"
				),
		},
		{
			title: "Precio Unitario",
			dataIndex: "Unit_Price",
			key: "Unit_Price",
			width: 120,
			render: (price, record) => formatPrice(price, record.Currency_Code),
		},
		{
			title: "Cantidad Mínima",
			dataIndex: "Minimum_Quantity",
			key: "Minimum_Quantity",
			width: 120,
			render: (quantity) =>
				quantity !== null && quantity !== undefined
					? Number(quantity).toFixed(2)
					: "-",
		},
		{
			title: "Unidad de Medida",
			dataIndex: "Unit_of_Measure_Code",
			key: "Unit_of_Measure_Code",
			width: 130,
			render: (text) => text || "-",
		},
		{
			title: "Código Variante",
			dataIndex: "Variant_Code",
			key: "Variant_Code",
			width: 120,
			render: (text) => text || "-",
		},
		{
			title: "Desc. Línea",
			dataIndex: "Allow_Line_Disc",
			key: "Allow_Line_Disc",
			width: 100,
			render: (allow) => (
				<Tag color={allow ? "green" : "red"}>{allow ? "Sí" : "No"}</Tag>
			),
		},
		{
			title: "Desc. Factura",
			dataIndex: "Allow_Invoice_Disc",
			key: "Allow_Invoice_Disc",
			width: 110,
			render: (allow) => (
				<Tag color={allow ? "green" : "red"}>{allow ? "Sí" : "No"}</Tag>
			),
		},
		{
			title: "Grupo IVA",
			dataIndex: "VAT_Bus_Posting_Gr_Price",
			key: "VAT_Bus_Posting_Gr_Price",
			width: 120,
			render: (text) => text || "-",
		},
	];

	if (loading) {
		return (
			<div style={{ textAlign: "center", padding: "50px" }}>
				<Spin size="large" />
				<p style={{ marginTop: "16px" }}>Cargando precios de venta...</p>
			</div>
		);
	}

	return (
		<div style={{ padding: isMobile ? "16px" : "24px" }}>
			<Card>
				<Row
					justify="space-between"
					align="middle"
					style={{ marginBottom: "24px" }}
				>
					<Col>
						<Title level={3} style={{ margin: 0 }}>
							Precios de Venta
						</Title>
					</Col>
					<Col>
						<Space>
							<Tooltip title="Actualizar lista">
								<Button
									icon={<ReloadOutlined />}
									onClick={loadSalesPrices}
									loading={loading}
								>
									Actualizar
								</Button>
							</Tooltip>
						</Space>
					</Col>
				</Row>

				{/* Filtros */}
				<Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
					<Col xs={24} sm={12} md={8} lg={6}>
						<Input
							placeholder="Buscar por artículo, código..."
							prefix={<SearchOutlined />}
							value={searchText}
							onChange={(e) => setSearchText(e.target.value)}
							allowClear
						/>
					</Col>
					<Col xs={24} sm={12} md={8} lg={6}>
						<Select
							placeholder="Filtrar por tipo de venta"
							style={{ width: "100%" }}
							value={filterSalesType}
							onChange={setFilterSalesType}
							allowClear
						>
							{uniqueSalesTypes.map((type) => (
								<Option key={type} value={type}>
									{type}
								</Option>
							))}
						</Select>
					</Col>
					<Col xs={24} sm={12} md={8} lg={6}>
						<Select
							placeholder="Filtrar por moneda"
							style={{ width: "100%" }}
							value={filterCurrency}
							onChange={setFilterCurrency}
							allowClear
						>
							{uniqueCurrencies.map((currency) => (
								<Option key={currency} value={currency}>
									{currency}
								</Option>
							))}
						</Select>
					</Col>
					<Col xs={24} sm={12} md={8} lg={6}>
						<Button
							icon={<FilterOutlined />}
							onClick={clearFilters}
							disabled={!searchText && !filterSalesType && !filterCurrency}
						>
							Limpiar Filtros
						</Button>
					</Col>
				</Row>

				{/* Tabla */}
				<Table
					columns={columns}
					dataSource={filteredData}
					rowKey={(record, index) =>
						`${record.Item_No}-${record.Sales_Type}-${record.Sales_Code}-${index}`
					}
					pagination={{
						total: filteredData.length,
						pageSize: 50,
						showSizeChanger: true,
						showQuickJumper: true,
						showTotal: (total, range) =>
							`${range[0]}-${range[1]} de ${total} precios`,
					}}
					scroll={{ x: 1400, y: isMobile ? 400 : 600 }}
					locale={{
						emptyText: (
							<Empty
								description="No hay precios de venta disponibles"
								image={Empty.PRESENTED_IMAGE_SIMPLE}
							/>
						),
					}}
					size={isMobile ? "small" : "middle"}
				/>

				{/* Información adicional */}
				<div style={{ marginTop: "16px", color: "#666" }}>
					<p>
						Total de precios: {filteredData.length}
						{filteredData.length !== salesPrices.length &&
							` (filtrados de ${salesPrices.length})`}
					</p>
				</div>
			</Card>
		</div>
	);
};

export default SalesPriceList;
